import { NavigateFn } from '@tanstack/react-router'
import { Column, ColumnDef, RowData } from '@tanstack/react-table'
import React, { createContext, useCallback, useContext } from 'react'

interface ColumnFilterContextValue<TData = RowData> {
  addFilter: (columnId: string) => void
  activeFilterIds: string[]
  setActiveFilterIds: React.Dispatch<React.SetStateAction<string[]>>
  getColumn: (columnId: string) => Column<TData, unknown> | undefined
  columns: ColumnDef<TData>[]
  navigate: NavigateFn
  disableUrlFilters: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ColumnFilterContext = createContext<ColumnFilterContextValue<any> | null>(
  null
)

export function useColumnFilterContext<
  TData = RowData,
>(): ColumnFilterContextValue<TData> | null {
  return useContext(ColumnFilterContext)
}

export const ColumnFilterProvider = <TData,>({
  activeFilterIds,
  setActiveFilterIds,
  getColumn,
  columns,
  navigate,
  disableUrlFilters,
  children,
}: {
  activeFilterIds: string[]
  setActiveFilterIds: React.Dispatch<React.SetStateAction<string[]>>
  getColumn: (columnId: string) => Column<TData, unknown> | undefined
  columns: ColumnDef<TData>[]
  navigate: NavigateFn
  disableUrlFilters: boolean
  children: React.ReactNode
}) => {
  const addFilter = useCallback(
    (columnId: string) => {
      setActiveFilterIds((prev) =>
        prev.includes(columnId) ? prev : [...prev, columnId]
      )
    },
    [setActiveFilterIds]
  )

  const value = React.useMemo(
    () => ({
      addFilter,
      activeFilterIds,
      setActiveFilterIds,
      getColumn,
      columns,
      navigate,
      disableUrlFilters,
    }),
    [
      addFilter,
      activeFilterIds,
      setActiveFilterIds,
      getColumn,
      columns,
      navigate,
      disableUrlFilters,
    ]
  )

  return (
    <ColumnFilterContext.Provider value={value}>
      {children}
    </ColumnFilterContext.Provider>
  )
}
